import arrowIosBackFill from '@iconify/icons-eva/arrow-ios-back-fill';
import closeFill from '@iconify/icons-eva/close-fill';
import { Icon } from '@iconify/react';
import { Box, Button, Card, Container, Link, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import verifyEmailIllustration from 'assets/verify_email_illustration.svg';
import MHidden from 'components/@material-extend/MHidden/MHidden';
import { IconButtonAnimate } from 'components/animate';
import VerifyCodeForm from 'components/auth/VerifyCode/VerifyCodeForm';
import Page from 'components/Page/Page';
import { sendConfirmationEmail } from 'external/firebase/controllers/auth';
import useAuth from 'hooks/useAuth';
import useLocales from 'hooks/useLocales';
import AuthLayout from 'layouts/AuthLayout';
import { PATH_AUTH } from 'routes/paths';

// ----------------------------------------------------------------------

const RootStyle = styled(Page)(({ theme }) => ({
    [theme.breakpoints.up('md')]: {
        display: 'flex',
    },
}));

const SectionStyle = styled(Card)(({ theme }) => ({
    width: '100%',
    maxWidth: 464,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    margin: theme.spacing(2, 0, 2, 2),
}));

const ContentStyle = styled('div')(({ theme }) => ({
    maxWidth: 480,
    margin: 'auto',
    display: 'flex',
    minHeight: '100vh',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: theme.spacing(12, 0),
}));

// ----------------------------------------------------------------------

const VerifyEmailAndCode: React.FunctionComponent = () => {
    const { user, profile } = useAuth();
    const { translate } = useLocales();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    const isVerified = Boolean(user?.emailVerified);
    const isStaff = Boolean(
        user?.email &&
            user.email.match(/@codeacademy.lt|@codeacademykids.lt|@codeacademykids.com|@codeacademylt.onmicrosoft.com/),
    );
    const hasRole = Boolean(profile?.role);

    const handleSendConfirmationEmail = async () => {
        try {
            await sendConfirmationEmail();
            enqueueSnackbar(translate('confirmation_email_success'), {
                variant: 'success',
                // eslint-disable-next-line react/display-name
                action: key => (
                    <IconButtonAnimate size='small' onClick={() => closeSnackbar(key)}>
                        <Icon icon={closeFill} />
                    </IconButtonAnimate>
                ),
            });
        } catch (err) {
            console.error(err);
            // FIXME: handle error
            enqueueSnackbar(translate('confirmation_email_failure'), {
                variant: 'error',
                // eslint-disable-next-line react/display-name
                action: key => (
                    <IconButtonAnimate size='small' onClick={() => closeSnackbar(key)}>
                        <Icon icon={closeFill} />
                    </IconButtonAnimate>
                ),
            });
        }
    };

    return (
        <RootStyle title={`${translate('verify')} | CodeAcademy Kids`}>
            <AuthLayout />

            <MHidden width='mdDown'>
                <SectionStyle>
                    <img src={verifyEmailIllustration} alt={translate('verify_email_and_code_illustration')} />
                </SectionStyle>
            </MHidden>

            <Container>
                <ContentStyle>
                    <Box sx={{ maxWidth: 480, mx: 'auto' }}>
                        <Button
                            size='small'
                            component={RouterLink}
                            to={PATH_AUTH.login}
                            startIcon={<Icon icon={arrowIosBackFill} width={20} height={20} />}
                            sx={{ mb: 3, textTransform: 'capitalize' }}
                        >
                            {translate('back')}
                        </Button>

                        <Typography variant='h3' paragraph>
                            {isVerified && isStaff && !hasRole
                                ? translate('verify_code_title')
                                : translate('verify_email_title')}
                        </Typography>
                        <Typography sx={{ color: 'text.secondary' }}>
                            {isVerified
                                ? translate('verify_code_subtitle')
                                : `${translate('verify_email_subtitle_part_1')} ${user?.email}, ` +
                                  `${translate('verify_email_subtitle_part_2')}`}
                        </Typography>
                        <Typography sx={{ color: 'text.secondary' }}>
                            {!isVerified && `${translate('check_spam_folder')}`}
                        </Typography>

                        <Box sx={{ mt: 5, mb: 3 }}>{isVerified && isStaff && !hasRole && <VerifyCodeForm />}</Box>

                        {!isVerified && (
                            <Typography variant='body2' align='center'>
                                {translate('didnt_receive_email')} &nbsp;
                                <Link
                                    variant='subtitle2'
                                    underline='none'
                                    onClick={handleSendConfirmationEmail}
                                    href='#'
                                >
                                    {translate('resend_email')}
                                </Link>
                            </Typography>
                        )}
                    </Box>
                </ContentStyle>
            </Container>
        </RootStyle>
    );
};

export default VerifyEmailAndCode;
